import { useEffect, useState, useCallback } from 'react';
import tabSessionService from '../services/tabSessionService';
import websocketService from '../services/websocketService';
import { getTabSessionUserKey } from '../utils/tabSessionUtils';

export const useTabSession = (enabled, userId, token) => {
  const [state, setState] = useState(() => tabSessionService.getState());

  useEffect(() => {
    if (!enabled) {
      websocketService.setTabSessionBlocked(false);
      tabSessionService.destroy();
      return undefined;
    }

    const sessionUserKey = userId || getTabSessionUserKey();
    if (!sessionUserKey) {
      return undefined;
    }

    tabSessionService.init(sessionUserKey);
    const unsubscribe = tabSessionService.subscribe(setState);

    return () => {
      unsubscribe();
      // React StrictMode remounts in dev — don't drop the lock or /auth/me gets blocked.
      tabSessionService.destroy({ releaseLock: false });
    };
  }, [enabled, userId]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (state.isSuperseded || state.showSessionConflict) {
      websocketService.setTabSessionBlocked(true);
      return;
    }

    websocketService.setTabSessionBlocked(false);
    if (token && !websocketService.isConnected()) {
      websocketService.connect(token);
    }
  }, [enabled, state.isSuperseded, state.showSessionConflict, state.isLeader, token]);

  const useWorkloopHere = useCallback(() => {
    tabSessionService.useWorkloopHere();
  }, []);

  const closeOtherSession = useCallback(() => {
    tabSessionService.closeOtherSession();
  }, []);

  const reclaimSession = useCallback(() => {
    tabSessionService.reclaimSession();
  }, []);

  const leaveToSessionClosedPage = useCallback(() => {
    tabSessionService.navigateToSessionClosedPage();
  }, []);

  return {
    ...state,
    useWorkloopHere,
    closeOtherSession,
    reclaimSession,
    leaveToSessionClosedPage,
  };
};

export default useTabSession;
